import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KeyboardEvent as ReactKeyboardEvent } from "react";
import {
  Activity,
  ArrowLeft,
  BellRing,
  Bot,
  BrainCircuit,
  Braces,
  BookOpenText,
  ChevronDown,
  Command,
  Compass,
  FilePlus2,
  Gauge,
  GitBranch,
  Globe2,
  HelpCircle,
  Inbox,
  Layers3,
  LoaderCircle,
  LogOut,
  Menu,
  PackageOpen,
  Orbit,
  PanelLeftClose,
  PanelLeftOpen,
  Plus,
  RefreshCw,
  Search,
  Settings,
  SlidersHorizontal,
  Sparkles,
  Star,
  WandSparkles,
  X,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { toast } from "./toast";
import { api } from "./api";
import { ApiError, type Principal, type PublicHealth } from "./api";
import type { TakeoutHousekeepingProps } from "./components/TakeoutHousekeeping";
import { Button, Modal } from "./components/UI";
import { useModalInteraction } from "./modal-interaction";
import { OverviewPage } from "./pages/OverviewPage";
import { useQolHistory, useQolPreferences, type QolHistory } from "./qol";
import { LATTICE_RELEASE_INFO, releaseBadge, releaseDescription, releaseIdentityLabel, releaseMismatch } from "./release";
import { useKnowledgeSearch } from "./search";
import { appRouteHash, parseAppRoute, type PageId } from "./routes";
import type { ActivityReceipt, Agent, ContextPack, Note, NoteDraft, TakeoutCandidate, TakeoutReviewPage } from "./types";
import { cx } from "./utils";
import { workspaceSection } from "./workspace-navigation";

import type { ActionInboxLane, PendingActionCounts } from "./pages/ActionInboxPage";

const ActiveMemoryPage = lazy(async () => {
  const module = await import("./pages/ActiveMemoryPage");
  return { default: module.ActiveMemoryPage };
});
const ActionInboxPage = lazy(async () => ({ default: (await import("./pages/ActionInboxPage")).ActionInboxPage }));
const WorkflowDeckPage = lazy(async () => ({ default: (await import("./pages/WorkflowDeckPage")).WorkflowDeckPage }));
const DiscoverPage = lazy(async () => ({ default: (await import("./pages/DiscoverPage")).DiscoverPage }));
const MemoryPage = lazy(async () => ({ default: (await import("./pages/MemoryPage")).MemoryPage }));
const GraphPage = lazy(async () => ({ default: (await import("./pages/GraphPage")).GraphPage }));
const WikiPage = lazy(async () => ({ default: (await import("./pages/WikiPage")).WikiPage }));
const LibraryPage = lazy(async () => ({ default: (await import("./pages/LibraryPage")).LibraryPage }));
const TakeoutCuratorPage = lazy(async () => ({ default: (await import("./pages/TakeoutCuratorPage")).TakeoutCuratorPage }));
const PacksPage = lazy(async () => ({ default: (await import("./pages/PacksPage")).PacksPage }));
const AgentsPage = lazy(async () => ({ default: (await import("./pages/AgentsPage")).AgentsPage }));
const ActivityPage = lazy(async () => ({ default: (await import("./pages/ActivityPage")).ActivityPage }));
const SettingsPage = lazy(async () => ({ default: (await import("./pages/SettingsPage")).SettingsPage }));
const AgentJoinPage = lazy(async () => ({ default: (await import("./pages/AgentJoinPage")).AgentJoinPage }));
const LoginPage = lazy(async () => ({ default: (await import("./components/LoginPage")).LoginPage }));
const NoteEditor = lazy(async () => ({ default: (await import("./components/NoteEditor")).NoteEditor }));
const CaptureModal = lazy(async () => ({ default: (await import("./components/CaptureModal")).CaptureModal }));
const PackBuilder = lazy(async () => ({ default: (await import("./components/PackBuilder")).PackBuilder }));
const AgentWizard = lazy(async () => ({ default: (await import("./components/AgentWizard")).AgentWizard }));
const QolPreferencesPanel = lazy(async () => ({ default: (await import("./components/QolPreferencesPanel")).QolPreferencesPanel }));
const ReleaseDetails = lazy(async () => ({ default: (await import("./components/ReleaseDetails")).ReleaseDetails }));
const WhatsNewNotice = lazy(async () => ({ default: (await import("./components/WhatsNewNotice")).WhatsNewNotice }));

function RouteLoader({ label = "Opening workspace…" }: { label?: string }) {
  return <div className="auth-loading" role="status"><LoaderCircle className="spin" size={20} /><small>{label}</small></div>;
}

const NAV_GROUPS: Array<{ label: string; items: Array<{ id: PageId; label: string; icon: LucideIcon }> }> = [
  { label: "Workspace", items: [{ id: "overview", label: "Home", icon: Gauge }, { id: "library", label: "Knowledge", icon: Braces }, { id: "packs", label: "Context", icon: PackageOpen }, { id: "review", label: "Review", icon: Inbox }, { id: "activity", label: "Activity", icon: Activity }] },
  { label: "Knowledge views", items: [{ id: "wiki", label: "Read & history", icon: BookOpenText }, { id: "graph", label: "Knowledge graph", icon: GitBranch }, { id: "discover", label: "Timeline & discovery", icon: Compass }] },
  { label: "Context tools", items: [{ id: "memory", label: "Prepare task context", icon: BrainCircuit }, { id: "agents", label: "Agent access", icon: Bot }, { id: "workflows", label: "Task templates", icon: WandSparkles }] },
  { label: "More tools", items: [{ id: "active", label: "Tasks & upkeep", icon: Orbit }, { id: "curator", label: "Import conversations", icon: Sparkles }] },
];

export default function App() {
  if (window.location.pathname.replace(/\/+$/, "") === "/join/agent") {
    return <Suspense fallback={<RouteLoader label="Opening agent invitation…" />}><AgentJoinPage /></Suspense>;
  }
  return <AuthenticatedApp />;
}

function AuthenticatedApp() {
  const [runtime, setRuntime] = useState<"loading" | "local" | "appwrite">("loading");
  const [principal, setPrincipal] = useState<Principal | null>(null);
  const [authError, setAuthError] = useState<string | null>(null);

  const bootstrap = useCallback(async () => {
    setRuntime("loading");
    setAuthError(null);
    try {
      const info = await api.runtime();
      if (!info.authRequired) {
        setRuntime("local");
        return;
      }
      setRuntime("appwrite");
      try {
        setPrincipal(await api.me());
      } catch (error) {
        if (error instanceof ApiError && (error.status === 401 || error.status === 403)) {
          setPrincipal(null);
          return;
        }
        throw error;
      }
    } catch (error) {
      setRuntime("appwrite");
      setAuthError(error instanceof Error ? error.message : "The identity service could not be reached.");
    }
  }, []);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  if (runtime === "loading") {
    return <div className="auth-loading"><img className="brand-logo" src="/brand-assets/lattice/logo.svg" width="35" height="35" alt="Lattice" /><LoaderCircle className="spin" size={20} /><small>Resolving workspace identity…</small></div>;
  }
  if (runtime === "appwrite" && (!principal || authError)) {
    return <Suspense fallback={<RouteLoader label="Opening secure sign in…" />}><LoginPage onAuthenticated={(next) => { setPrincipal(next); setAuthError(null); }} serviceUnavailable={authError} onRetry={() => void bootstrap()} /></Suspense>;
  }
  return (
    <WorkspaceApp
      principal={principal}
      onLogout={principal ? async () => { await api.logout(); setPrincipal(null); } : undefined}
    />
  );
}

function WorkspaceApp({ principal, onLogout }: { principal: Principal | null; onLogout?: () => Promise<void> }) {
  const initialRoute = useMemo(() => parseAppRoute(window.location.hash), []);
  const [page, setPage] = useState<PageId>(initialRoute.page);
  const [wikiRouteId, setWikiRouteId] = useState<string | undefined>(initialRoute.wikiNoteId);
  const [mobileNav, setMobileNav] = useState(false);
  const [commandOpen, setCommandOpen] = useState(false);
  const [captureOpen, setCaptureOpen] = useState(false);
  const [noteEditor, setNoteEditor] = useState<{ note?: Note | null; initialTitle?: string; draft?: NoteDraft }>();
  const [packBuilder, setPackBuilder] = useState<{ pack?: ContextPack | null; noteIds?: string[]; notes: Note[]; agents: Agent[] }>();
  const [agentWizard, setAgentWizard] = useState<{ packs: ContextPack[] }>();
  const [replay, setReplay] = useState<ActivityReceipt | null>(null);
  const [graphFocusId, setGraphFocusId] = useState<string>();
  const [refreshKey, setRefreshKey] = useState(0);
  const [online, setOnline] = useState<boolean | null>(null);
  const [serverRelease, setServerRelease] = useState<PublicHealth>();
  const [healthCheckKey, setHealthCheckKey] = useState(0);
  const [agentCount, setAgentCount] = useState(0);
  const [packCount, setPackCount] = useState(0);
  const [helpOpen, setHelpOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [qolOpen, setQolOpen] = useState(false);
  const [takeoutCategory, setTakeoutCategory] = useState("all");
  const [actionLane, setActionLane] = useState<ActionInboxLane>();
  const [reviewReturnKey, setReviewReturnKey] = useState(0);
  const [pendingActions, setPendingActions] = useState<PendingActionCounts>({ proposals: 0, enrollments: 0, packRequests: 0, total: 0 });
  const pendingActionsRef = useRef(pendingActions);
  const shellRequestRef = useRef<AbortController | undefined>(undefined);
  const [shellLowerBounds, setShellLowerBounds] = useState({ pendingActions: false, contextPacks: false });
  const [lastRefreshAt, setLastRefreshAt] = useState(() => new Date());
  const [routeAnnouncement, setRouteAnnouncement] = useState("Overview opened");
  const mainRef = useRef<HTMLElement>(null);
  const scrollPositions = useRef(new Map<PageId, number>());
  const pendingScroll = useRef(0);
  const updateAvailable = releaseMismatch(serverRelease);
  const [takeoutReview, setTakeoutReview] = useState<{
    items: TakeoutCandidate[];
    totalPending: number;
    facets: TakeoutReviewPage["facets"];
    loading: boolean;
    error?: string;
    busyCandidateId?: string;
    reloadNonce: number;
  }>({
    items: [],
    totalPending: 0,
    facets: { kinds: [], tags: [], taxonomy: [], years: [], imports: [] },
    loading: false,
    reloadNonce: 0,
  });
  const canEdit = !principal || ["editor", "admin", "owner"].includes(principal.role);
  const canAdmin = !principal || ["admin", "owner"].includes(principal.role);
  const isOwner = !principal || principal.role === "owner";
  const workspaceName = principal?.team.name ?? "Local Vault";
  const qolScope = `${principal?.team.id ?? "local-vault"}:${principal?.user.id ?? "local-owner"}`;
  const { preferences, patchPreferences, resetPreferences } = useQolPreferences(qolScope);
  const { history: qolHistory, recordPage, recordNote, recordSearch, toggleFavoritePage } = useQolHistory(qolScope);
  const visibleNavGroups = useMemo(
    () => NAV_GROUPS.map((group) => ({ ...group, items: group.items.filter((item) => (item.id !== "agents" || canAdmin) && (item.id !== "curator" || canEdit) && (item.id !== "review" || canEdit)) })),
    [canAdmin, canEdit],
  );

  const refresh = useCallback(() => {
    setRefreshKey((value) => value + 1);
    setLastRefreshAt(new Date());
  }, []);

  const navigate = useCallback((next: string, receipt?: ActivityReceipt, reviewLane?: ActionInboxLane) => {
    const target = next as PageId;
    if (target === "review") {
      setActionLane(reviewLane);
      if (page === "review") setReviewReturnKey((value) => value + 1);
    }
    scrollPositions.current.set(page, window.scrollY);
    pendingScroll.current = 0;
    window.history.pushState(null, "", appRouteHash(target));
    setPage(target);
    setWikiRouteId(undefined);
    setMobileNav(false);
    if (receipt) setReplay(receipt);
  }, [page]);

  const navigateWikiPage = useCallback((noteId?: string) => {
    scrollPositions.current.set(page, window.scrollY);
    pendingScroll.current = 0;
    window.history.pushState(null, "", appRouteHash("wiki", noteId));
    setPage("wiki");
    setWikiRouteId(noteId);
    setMobileNav(false);
  }, [page]);

  const openWikiPage = useCallback((noteId: string) => {
    setNoteEditor(undefined);
    navigateWikiPage(noteId);
  }, [navigateWikiPage]);

  const openActionInbox = useCallback((lane?: ActionInboxLane) => {
    navigate("review", undefined, lane);
  }, [navigate]);

  useEffect(() => { pendingActionsRef.current = pendingActions; }, [pendingActions]);

  const loadWorkspaceShell = useCallback(async () => {
    shellRequestRef.current?.abort();
    const controller = new AbortController();
    shellRequestRef.current = controller;
    const summary = await api.workspaceShell(controller.signal);
    if (controller.signal.aborted) return;
    const next = summary.pendingActions;
    pendingActionsRef.current = next;
    setPendingActions(next);
    setAgentCount(summary.activeAgentCount);
    setPackCount(summary.contextPackCount);
    setShellLowerBounds(summary.lowerBounds);
    const announced = Number(sessionStorage.getItem("lattice:action-inbox:announced") ?? "-1");
    if (next.total > 0 && next.total > announced) {
      toast.info(`${summary.lowerBounds.pendingActions ? "At least " : ""}${next.total} ${next.total === 1 ? "action needs" : "actions need"} your review`);
      sessionStorage.setItem("lattice:action-inbox:announced", String(next.total));
    } else if (next.total < announced) {
      sessionStorage.setItem("lattice:action-inbox:announced", String(next.total));
    }
  }, []);

  useEffect(() => {
    void loadWorkspaceShell().catch(() => undefined);
    const interval = window.setInterval(() => void loadWorkspaceShell().catch(() => undefined), 30_000);
    const onVisible = () => { if (document.visibilityState === "visible") void loadWorkspaceShell().catch(() => undefined); };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      shellRequestRef.current?.abort();
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [loadWorkspaceShell, refreshKey]);

  const loadTakeoutHousekeeping = useCallback(async (category: string, signal?: AbortSignal) => {
    setTakeoutReview((current) => ({ ...current, loading: true, error: undefined }));
    try {
      const page = await api.takeoutReviewCandidates({
        status: "pending",
        recommendation: "all",
        tag: category === "all" ? undefined : category,
        limit: 25,
        offset: 0,
      }, { signal });
      const seenConversations = new Set<string>();
      const round = page.items.filter((candidate) => {
        if (seenConversations.has(candidate.conversationId)) return false;
        seenConversations.add(candidate.conversationId);
        return true;
      }).slice(0, 5);
      setTakeoutReview((current) => ({
        ...current,
        items: round,
        totalPending: page.stats.pending,
        facets: page.facets,
        loading: false,
        error: page.truncated
          ? "The archive view reached its safety bound. Open Takeout Curator and narrow by context for complete corpus-wide review."
          : undefined,
      }));
    } catch (caught) {
      if (caught instanceof DOMException && caught.name === "AbortError") return;
      setTakeoutReview((current) => ({
        ...current,
        loading: false,
        error: caught instanceof Error ? caught.message : "Could not choose a Takeout review set.",
      }));
    }
  }, []);

  useEffect(() => {
    if (!canEdit || page !== "overview") return;
    const controller = new AbortController();
    void loadTakeoutHousekeeping(takeoutCategory, controller.signal);
    return () => controller.abort();
  }, [canEdit, loadTakeoutHousekeeping, page, takeoutCategory, takeoutReview.reloadNonce]);

  const reviewTakeoutCandidate = useCallback(async (candidate: TakeoutCandidate, decision: "accept" | "reject") => {
    setTakeoutReview((current) => ({ ...current, busyCandidateId: candidate.id }));
    try {
      if (decision === "accept") await api.acceptTakeoutCandidate(candidate.id, {});
      else await api.rejectTakeoutCandidate(candidate.id);
      setTakeoutReview((current) => ({
        ...current,
        items: current.items.filter((item) => item.id !== candidate.id),
        totalPending: Math.max(0, current.totalPending - 1),
        busyCandidateId: undefined,
      }));
      refresh();
    } catch (caught) {
      setTakeoutReview((current) => ({ ...current, busyCandidateId: undefined }));
      throw caught;
    }
  }, [refresh]);

  const takeoutHousekeeping = useMemo<TakeoutHousekeepingProps | undefined>(() => {
    if (!canEdit) return undefined;
    const categoryOptions = [
      { value: "all", label: "Recommended first", count: takeoutReview.totalPending },
      ...takeoutReview.facets.tags.slice(0, 14).map((facet) => ({
        value: facet.value,
        label: `#${facet.label}`,
        count: facet.count,
      })),
    ];
    return {
      items: takeoutReview.items,
      totalPending: takeoutReview.totalPending,
      loading: takeoutReview.loading,
      error: takeoutReview.error,
      busyCandidateId: takeoutReview.busyCandidateId,
      roundLabel: "Five from your archive",
      categoryLabel: takeoutCategory === "all" ? "Recommended first" : `#${takeoutCategory}`,
      categoryOptions,
      activeCategory: takeoutCategory,
      onCategoryChange: (category) => {
        setTakeoutReview((current) => ({ ...current, loading: true, error: undefined }));
        setTakeoutCategory(category);
      },
      onAccept: (candidate) => reviewTakeoutCandidate(candidate, "accept"),
      onReject: (candidate) => reviewTakeoutCandidate(candidate, "reject"),
      onEdit: (candidate) => {
        sessionStorage.setItem("lattice:takeout:selected-candidate", candidate.id);
        navigate("curator");
      },
      onOpenCurator: () => navigate("curator"),
      onReviewMore: () => {
        setTakeoutReview((current) => ({ ...current, loading: true, reloadNonce: current.reloadNonce + 1 }));
      },
      onRetry: () => void loadTakeoutHousekeeping(takeoutCategory),
    };
  }, [canEdit, loadTakeoutHousekeeping, navigate, reviewTakeoutCandidate, takeoutCategory, takeoutReview]);

  useEffect(() => {
    const onHash = () => {
      scrollPositions.current.set(page, window.scrollY);
      const next = parseAppRoute(window.location.hash);
      pendingScroll.current = scrollPositions.current.get(next.page) ?? 0;
      setPage(next.page);
      setWikiRouteId(next.wikiNoteId);
    };
    window.addEventListener("hashchange", onHash);
    window.addEventListener("popstate", onHash);
    return () => {
      window.removeEventListener("hashchange", onHash);
      window.removeEventListener("popstate", onHash);
    };
  }, [page]);

  const section = workspaceSection(page);
  const pageTitle = useMemo(() => visibleNavGroups.flatMap((group) => group.items).find((item) => item.id === page)?.label ?? (page === "settings" ? "Settings" : "Home"), [page, visibleNavGroups]);

  useEffect(() => {
    document.title = `${pendingActions.total ? `(${pendingActions.total}${shellLowerBounds.pendingActions ? "+" : ""}) ` : ""}${pageTitle} · Lattice`;
  }, [pageTitle, pendingActions.total, shellLowerBounds.pendingActions]);

  useEffect(() => {
    setRouteAnnouncement(`${pageTitle} opened`);
    recordPage(page);
    const frame = window.requestAnimationFrame(() => {
      mainRef.current?.focus({ preventScroll: true });
      if (!navigator.userAgent.includes("jsdom")) {
        window.scrollTo({ top: pendingScroll.current, behavior: preferences.motion === "reduced" ? "auto" : "smooth" });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [page, pageTitle, preferences.motion, recordPage, reviewReturnKey]);

  useEffect(() => {
    if ((!canAdmin && (page === "agents" || page === "settings")) || (!canEdit && (page === "curator" || page === "review"))) {
      window.history.replaceState(null, "", appRouteHash("overview"));
      setPage("overview");
      setWikiRouteId(undefined);
    }
  }, [canAdmin, canEdit, page]);

  useEffect(() => {
    let active = true;
    let controller: AbortController | undefined;
    async function status() {
      controller?.abort();
      const requestController = new AbortController();
      controller = requestController;
      try {
        const health = await api.publicReady(requestController.signal);
        if (!active) return;
        setServerRelease(health);
        setOnline(true);
      } catch {
        if (active && !requestController.signal.aborted) setOnline(false);
      }
    }
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void status();
    };
    void status();
    const interval = window.setInterval(status, 30000);
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () => {
      active = false;
      controller?.abort();
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [healthCheckKey]);

  useEffect(() => {
    if (!preferences.autoRefresh) return;
    const interval = window.setInterval(refresh, 60_000);
    return () => window.clearInterval(interval);
  }, [preferences.autoRefresh, refresh]);

  useEffect(() => {
    const listener = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;
      const editing = ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName) || target.isContentEditable;
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setCommandOpen(true);
      } else if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "p") {
        event.preventDefault();
        setCommandOpen(true);
      } else if ((event.metaKey || event.ctrlKey) && event.shiftKey && event.key.toLowerCase() === "r") {
        event.preventDefault();
        refresh();
      } else if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "b") {
        event.preventDefault();
        patchPreferences({ sidebarCollapsed: !preferences.sidebarCollapsed });
      } else if (!editing && event.altKey && /^[1-9]$/.test(event.key)) {
        const item = visibleNavGroups.flatMap((group) => group.items)[Number(event.key) - 1];
        if (item) {
          event.preventDefault();
          navigate(item.id);
        }
      } else if (!editing && event.key === "/") {
        event.preventDefault();
        setCommandOpen(true);
      } else if (!editing && event.key === "?") {
        event.preventDefault();
        setHelpOpen(true);
      } else if (canEdit && !editing && event.key.toLowerCase() === "n") {
        setNoteEditor({ note: null });
      } else if (canEdit && !editing && event.key.toLowerCase() === "c") {
        setCaptureOpen(true);
      }
    };
    window.addEventListener("keydown", listener);
    return () => window.removeEventListener("keydown", listener);
  }, [canEdit, navigate, patchPreferences, preferences.sidebarCollapsed, refresh, visibleNavGroups]);

  async function openNote(id: string) {
    try {
      const note = await api.note(id);
      recordNote({ id: note.id, title: note.title, type: note.type });
      setNoteEditor({ note });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not open note");
    }
  }

  async function openWikiLink(title: string) {
    try {
      const results = await api.notes({ query: title });
      const match = results.find((note) => note.title.toLowerCase() === title.toLowerCase());
      if (match) {
        const note = await api.note(match.id);
        recordNote({ id: note.id, title: note.title, type: note.type });
        setNoteEditor({ note });
      }
      else if (canEdit) setNoteEditor({ note: null, initialTitle: title });
      else toast.info("That wikilink does not resolve to an accessible note.");
    } catch {
      if (canEdit) setNoteEditor({ note: null, initialTitle: title });
      else toast.error("Could not open that note.");
    }
  }

  async function openPackBuilder(pack?: ContextPack | null, noteIds: string[] = []) {
    try {
      const requestedIds = [...new Set([...(pack?.noteIds ?? []), ...noteIds])];
      const [pageNotes, selectedNotes, agents] = await Promise.all([
        api.notes({ limit: 250 }),
        Promise.all(requestedIds.map((id) => api.note(id).catch(() => null))),
        canAdmin ? api.agents() : Promise.resolve([]),
      ]);
      const selectedValid: Note[] = selectedNotes.flatMap((note) => note ? [note as Note] : []);
      const notes = [...new Map([...pageNotes, ...selectedValid].map((note) => [note.id, note])).values()];
      setPackBuilder({ pack, noteIds, notes, agents: agents.map((agent: any) => ({ ...agent, permissions: agent.permissions ?? agent.scopes ?? [] })) });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not open Context Pack builder");
    }
  }

  async function openAgentWizard() {
    try {
      setAgentWizard({ packs: await api.packs() });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not load Context Packs");
    }
  }

  return (
    <div className={cx("app-shell", preferences.sidebarCollapsed && "sidebar-collapsed")}>
      <a className="skip-link" href="#workspace-main">Skip to workspace</a>
      <aside className={cx("sidebar", mobileNav && "mobile-open")} aria-label="Workspace navigation">
        <div className="brand">
          <img className="brand-logo" src="/brand-assets/lattice/logo.svg" width="35" height="35" alt="Lattice" />
          <div><strong>LATTICE</strong><small>connected knowledge</small></div>
          <Button variant="ghost" size="icon" className="mobile-close" aria-label="Close navigation" onClick={() => setMobileNav(false)}><X size={18} /></Button>
        </div>

        <div className="workspace-switcher" aria-label="Current workspace">
          <span>LV</span>
          <div><strong>{workspaceName}</strong><small>{online === true ? "Workspace connected" : online === false ? "service offline" : "checking service"}</small></div>
          </div>

        <nav>
          {qolHistory.favoritePages.length ? <div className="nav-group nav-favorites"><span>Favorites</span>{qolHistory.favoritePages.flatMap((favorite) => {
            const item = NAV_GROUPS.flatMap((group) => group.items).find((candidate) => candidate.id === favorite);
            if (!item || (item.id === "agents" && !canAdmin) || ((item.id === "curator" || item.id === "review") && !canEdit)) return [];
            const Icon = item.icon;
            return <button key={item.id} type="button" aria-current={page === item.id ? "page" : undefined} className={page === item.id ? "active" : ""} onClick={() => navigate(item.id)}><Icon size={17} /><strong>{item.label}</strong><Star size={12} fill="currentColor" /></button>;
          })}</div> : null}
          {visibleNavGroups.slice(0, 1).map((group) => (
            <div className="nav-group" key={group.label}>
              <span>{group.label}</span>
              {group.items.map(({ id, label, icon: Icon }) => (
                <button key={id} type="button" aria-current={page === id ? "page" : undefined} className={page === id || (id === "library" && section?.title === "Knowledge") || (id === "packs" && section?.title === "Context") || (id === "review" && section?.title === "Review") || (id === "activity" && section?.title === "Activity") ? "active" : ""} onClick={() => navigate(id)}>
                  <Icon size={17} />
                  <strong>{label}</strong>
                  {id === "packs" && packCount ? <small>{packCount}{shellLowerBounds.contextPacks ? "+" : ""}</small> : null}
                  {id === "agents" && agentCount ? <small className="live-count"><i />{agentCount}</small> : null}
                  {id === "review" && pendingActions.total ? <small className="action-count">{pendingActions.total}{shellLowerBounds.pendingActions ? "+" : ""}</small> : null}
                </button>
              ))}
            </div>
          ))}
          <details className="nav-tools"><summary>All tools <ChevronDown size={14} /></summary>{visibleNavGroups.slice(1).map((group) => <div className="nav-group" key={group.label}><span>{group.label}</span>{group.items.map(({ id, label, icon: Icon }) => <button key={id} type="button" aria-current={page === id ? "page" : undefined} className={page === id ? "active" : ""} onClick={() => navigate(id)}><Icon size={17} /><strong>{label}</strong></button>)}</div>)}</details>
        </nav>

        <div className="sidebar-bottom">
          <button type="button" className="sidebar-collapse-control" onClick={() => patchPreferences({ sidebarCollapsed: !preferences.sidebarCollapsed })} aria-label={preferences.sidebarCollapsed ? "Expand sidebar" : "Collapse sidebar"}>{preferences.sidebarCollapsed ? <PanelLeftOpen size={17} /> : <PanelLeftClose size={17} />}<strong>{preferences.sidebarCollapsed ? "Expand" : "Collapse"}</strong></button>
          <button type="button" onClick={() => setQolOpen(true)}><SlidersHorizontal size={17} /><strong>Display & motion</strong></button>
          {canAdmin ? <button type="button" className={page === "settings" ? "active" : ""} onClick={() => navigate("settings")}><Settings size={17} /><strong>Settings</strong></button> : null}
          <button type="button" onClick={() => setHelpOpen(true)}><HelpCircle size={17} /><strong>Keyboard & help</strong></button>
          {onLogout ? <button type="button" onClick={() => void onLogout()}><LogOut size={17} /><strong>Sign out</strong></button> : null}
          <div className="service-state"><span className={online === true ? "online" : online === false ? "offline" : "checking"}><i />{online === true ? "Connected" : online === false ? "API disconnected" : "Checking service"}</span><button type="button" className="release-version" title={releaseDescription()} aria-label={`Open build details: ${releaseDescription()}`} onClick={() => setReleaseOpen(true)}>{releaseBadge()}</button></div>
        </div>
      </aside>

      {mobileNav ? <button className="mobile-scrim" type="button" aria-label="Close navigation" onClick={() => setMobileNav(false)} /> : null}

      <div className="app-main">
        {online === false ? <div className="offline-banner" role="alert"><span><i />Shared context is unavailable. Lattice or Appwrite may be disconnected.</span><Button size="sm" variant="secondary" onClick={() => { refresh(); setHealthCheckKey((value) => value + 1); }}><RefreshCw size={14} /> Retry</Button></div> : null}
        {online === true && updateAvailable && serverRelease ? <div className="release-update-banner" role="status"><span><RefreshCw size={16} /><span><strong>Lattice changed on the server</strong><small>This tab is {releaseIdentityLabel(LATTICE_RELEASE_INFO)}; the server is {releaseIdentityLabel(serverRelease)}.</small></span></span><Button size="sm" variant="secondary" onClick={() => window.location.reload()}><RefreshCw size={14} /> Reload now</Button></div> : null}
        <header className="topbar">
          <Button variant="ghost" size="icon" className="mobile-menu" aria-label="Open navigation" onClick={() => setMobileNav(true)}><Menu size={20} /></Button>
          <Button variant="ghost" size="icon" className="history-back" aria-label="Go back" onClick={() => window.history.back()}><ArrowLeft size={18} /></Button>
          <div className="breadcrumb"><span>{workspaceName}</span><i>/</i><strong>{pageTitle}</strong></div>
          <button type="button" className="global-search" aria-label="Search notes, packs, and agents" onClick={() => setCommandOpen(true)}><Search size={16} /><span>Search knowledge…</span><kbd>{navigator.platform.includes("Mac") ? "⌘" : "Ctrl"} K</kbd></button>
          <div className="topbar-presence" aria-hidden="true"><span className="presence-orbits"><i /><i /><i /></span><div><strong>{agentCount} agents</strong><small>{online === true ? "shared context ready" : online === false ? "offline" : "checking"}</small></div></div>
          {canEdit ? <Button variant={pendingActions.total ? "secondary" : "ghost"} size="icon" className={pendingActions.total ? "topbar-action-inbox has-actions" : "topbar-action-inbox"} aria-label={pendingActions.total ? `Open Action Inbox: ${pendingActions.total}${shellLowerBounds.pendingActions ? "+" : ""} pending` : "Open Action Inbox"} onClick={() => openActionInbox()}><BellRing size={17} />{pendingActions.total ? <span>{pendingActions.total}{shellLowerBounds.pendingActions ? "+" : ""}</span> : null}</Button> : null}
          <Button variant="ghost" size="icon" className="topbar-refresh" aria-label={`Refresh workspace. Last refreshed ${lastRefreshAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`} title={`Last refreshed ${lastRefreshAt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`} onClick={refresh}><RefreshCw size={16} /></Button>
          <Button variant={qolHistory.favoritePages.includes(page) ? "secondary" : "ghost"} size="icon" className="topbar-favorite" aria-label={qolHistory.favoritePages.includes(page) ? `Remove ${pageTitle} from favorites` : `Add ${pageTitle} to favorites`} onClick={() => toggleFavoritePage(page)}><Star size={16} fill={qolHistory.favoritePages.includes(page) ? "currentColor" : "none"} /></Button>
          {canEdit ? <Button variant="secondary" size="sm" onClick={() => setCaptureOpen(true)}><Globe2 size={15} /> Save web page</Button> : null}
          {canEdit ? <Button variant="primary" size="sm" onClick={() => setNoteEditor({ note: null })}><Plus size={16} /> Write note</Button> : null}
        </header>

        {canEdit && pendingActions.total > 0 && page !== "review" ? <button type="button" className="action-alert-banner" onClick={() => openActionInbox(pendingActions.proposals ? "proposals" : pendingActions.enrollments ? "access" : "context")}><span><BellRing size={17} /><strong>{shellLowerBounds.pendingActions ? "At least " : ""}{pendingActions.total} {pendingActions.total === 1 ? "decision needs" : "decisions need"} your attention</strong><small>{pendingActions.proposals ? `${pendingActions.proposals}${shellLowerBounds.pendingActions ? "+" : ""} agent ${pendingActions.proposals === 1 ? "proposal" : "proposals"}` : ""}{pendingActions.proposals && (pendingActions.enrollments || pendingActions.packRequests) ? " · " : ""}{pendingActions.enrollments ? `${pendingActions.enrollments} access ${pendingActions.enrollments === 1 ? "request" : "requests"}` : ""}{pendingActions.enrollments && pendingActions.packRequests ? " · " : ""}{pendingActions.packRequests ? `${pendingActions.packRequests} context ${pendingActions.packRequests === 1 ? "request" : "requests"}` : ""}</small></span><span>Review now <ArrowLeft className="action-alert-arrow" size={15} /></span></button> : null}

        {online === true && !updateAvailable ? <Suspense fallback={null}><WhatsNewNotice scope={qolScope} version={LATTICE_RELEASE_INFO.version} release={LATTICE_RELEASE_INFO.release} onOpen={() => setReleaseOpen(true)} /></Suspense> : null}

        <main id="workspace-main" ref={mainRef} tabIndex={-1}>
          {section ? <div className="workspace-section"><nav aria-label={`${section.title} views`}>{section.views.filter((view) => (view.id !== "agents" || canAdmin) && (view.id !== "curator" || canEdit)).map((view) => <button type="button" key={view.id} aria-current={page === view.id ? "page" : undefined} onClick={() => navigate(view.id)}>{view.label}</button>)}</nav><details className="workspace-guide"><summary>How this works</summary><p>{section.help}</p></details></div> : null}
          <Suspense fallback={<RouteLoader />}>
          {page === "overview" ? <OverviewPage canEdit={canEdit} refreshKey={refreshKey} pendingActions={pendingActions} takeoutHousekeeping={takeoutHousekeeping} onNavigate={navigate} onOpenActionInbox={openActionInbox} onOpenNote={(id) => void openNote(id)} /> : null}
          {page === "review" ? <ActionInboxPage refreshKey={refreshKey} initialLane={actionLane} canAdmin={canAdmin} isOwner={isOwner} onCountsChange={(counts) => { setPendingActions(counts); setShellLowerBounds((current) => ({ ...current, pendingActions: false })); }} onChanged={refresh} /> : null}
          {page === "active" ? <ActiveMemoryPage refreshKey={refreshKey} canEdit={canEdit} canAdmin={canAdmin} onOpenNote={(id) => void openNote(id)} onBuildPack={(noteIds) => void openPackBuilder(null, noteIds)} /> : null}
          {page === "workflows" ? <WorkflowDeckPage canEdit={canEdit} canAdmin={canAdmin} qolScope={qolScope} onEditDraft={(draft) => setNoteEditor({ note: null, draft })} onOpenNote={(id) => void openNote(id)} onBuildPack={(noteIds) => void openPackBuilder(null, noteIds)} /> : null}
          {page === "discover" ? <DiscoverPage ritualScope={`${principal?.team.id ?? "local-vault"}:${principal?.user.id ?? "local-owner"}`} refreshKey={refreshKey} onOpenNote={(id) => void openNote(id)} onOpenGraph={(id) => { setGraphFocusId(id); navigate("graph"); }} /> : null}
          {page === "memory" ? <MemoryPage canEdit={canEdit} onOpenNote={(id) => void openNote(id)} onOpenWiki={openWikiPage} onBuildPack={(noteIds) => void openPackBuilder(null, noteIds)} onEditDraft={(draft) => setNoteEditor({ note: null, draft })} /> : null}
          {page === "graph" ? <GraphPage qolScope={qolScope} canEdit={canEdit} refreshKey={refreshKey} replay={replay} initialFocusId={graphFocusId} onConsumeInitialFocus={() => setGraphFocusId(undefined)} onClearReplay={() => setReplay(null)} onOpenNote={(id) => void openNote(id)} onOpenWiki={openWikiPage} onBuildPack={(noteIds) => void openPackBuilder(null, noteIds)} /> : null}
          {page === "wiki" ? <WikiPage canEdit={canEdit} refreshKey={refreshKey} initialNoteId={wikiRouteId} onNavigatePage={navigateWikiPage} onEditNote={(id) => void openNote(id)} /> : null}
          {page === "library" ? <LibraryPage qolScope={qolScope} canEdit={canEdit} refreshKey={refreshKey} onOpenNote={(id) => void openNote(id)} onOpenWiki={openWikiPage} onNewNote={() => setNoteEditor({ note: null })} onCapture={() => setCaptureOpen(true)} onChanged={refresh} /> : null}
          {page === "curator" ? <TakeoutCuratorPage refreshKey={refreshKey} onChanged={refresh} /> : null}
          {page === "packs" ? <PacksPage onOpenNote={(id) => void openNote(id)} onBuildContext={() => navigate("memory")} canEdit={canEdit} canAdmin={canAdmin} refreshKey={refreshKey} onBuildPack={(pack) => void openPackBuilder(pack)} /> : null}
          {page === "agents" ? <AgentsPage refreshKey={refreshKey} onConnect={() => void openAgentWizard()} onOpenActivity={(receipt) => receipt ? navigate("graph", receipt) : navigate("activity")} isOwner={isOwner} /> : null}
          {page === "activity" ? <ActivityPage onOpenHistory={() => navigate("wiki")} refreshKey={refreshKey} onReplay={(receipt) => navigate("graph", receipt)} onOpenNote={(id) => void openNote(id)} /> : null}
          {page === "settings" ? <SettingsPage refreshKey={refreshKey} onChanged={refresh} preferences={preferences} onPreferencesChange={patchPreferences} onPreferencesReset={resetPreferences} /> : null}
        </Suspense></main>
      </div>

      <nav className={canEdit ? "mobile-quick-nav has-action-inbox" : "mobile-quick-nav"} aria-label="Mobile quick navigation">
        {NAV_GROUPS[0].items.filter((item) => item.id !== "review" || canEdit).map(({ id, label, icon: Icon }) => <button type="button" key={id} aria-current={page === id ? "page" : undefined} className={`${page === id || (id === "library" && section?.title === "Knowledge") || (id === "packs" && section?.title === "Context") || (id === "activity" && section?.title === "Activity") ? "active" : ""}${id === "review" && pendingActions.total ? " has-actions" : ""}`} onClick={() => id === "review" ? openActionInbox() : navigate(id)}><Icon size={19} /><span>{label}</span>{id === "review" && pendingActions.total ? <b>{pendingActions.total}{shellLowerBounds.pendingActions ? "+" : ""}</b> : null}</button>)}
        {!canEdit ? <button type="button" aria-label="Open all navigation" aria-expanded={mobileNav} onClick={() => setMobileNav(true)}><Menu size={19} /><span>More</span></button> : null}
      </nav>
      <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">{routeAnnouncement}</div>

      <Suspense fallback={<RouteLoader label="Opening editor…" />}>
        {noteEditor ? <NoteEditor readOnly={!canEdit} note={noteEditor.note} initialTitle={noteEditor.initialTitle} draft={noteEditor.draft} onClose={() => setNoteEditor(undefined)} onSaved={(note) => { setNoteEditor({ note }); refresh(); }} onOpenWikiLink={(title) => void openWikiLink(title)} onOpenWikiPage={openWikiPage} /> : null}
        {captureOpen ? <CaptureModal onClose={() => setCaptureOpen(false)} onCaptured={(note) => { refresh(); void openNote(note.id); }} /> : null}
        {packBuilder ? <PackBuilder pack={packBuilder.pack} initialNoteIds={packBuilder.noteIds} notes={packBuilder.notes} agents={packBuilder.agents} canManageAgents={canAdmin} onClose={() => setPackBuilder(undefined)} onSaved={() => refresh()} /> : null}
        {agentWizard ? <AgentWizard packs={agentWizard.packs} onClose={() => setAgentWizard(undefined)} onCreated={() => refresh()} /> : null}
      </Suspense>
      {commandOpen ? <CommandPalette canEdit={canEdit} canAdmin={canAdmin} currentPage={page} history={qolHistory} onRecordSearch={recordSearch} onClose={() => setCommandOpen(false)} onNavigate={navigate} onOpenNote={(id) => void openNote(id)} onNewNote={(title) => setNoteEditor({ note: null, initialTitle: title })} onCapture={() => setCaptureOpen(true)} onPack={() => void openPackBuilder()} /> : null}
      {qolOpen ? <Modal title="Make Lattice comfortable" eyebrow="Your interface" onClose={() => setQolOpen(false)}><Suspense fallback={<RouteLoader label="Opening comfort controls…" />}><QolPreferencesPanel preferences={preferences} onChange={patchPreferences} onReset={resetPreferences} /></Suspense></Modal> : null}
      {releaseOpen ? <Modal title={`Lattice ${LATTICE_RELEASE_INFO.version}`} eyebrow="Running build" onClose={() => setReleaseOpen(false)} wide><Suspense fallback={<RouteLoader label="Opening improvement provenance…" />}><ReleaseDetails version={LATTICE_RELEASE_INFO.version} release={LATTICE_RELEASE_INFO.release} revision={LATTICE_RELEASE_INFO.revision} serverLabel={serverRelease ? `${releaseIdentityLabel(serverRelease)}${updateAvailable ? " · reload required" : " · matches this tab"}` : "Checking…"} /></Suspense></Modal> : null}
      {helpOpen ? <HelpModal onClose={() => setHelpOpen(false)} /> : null}
    </div>
  );
}

function CommandPalette({
  onClose,
  onNavigate,
  onOpenNote,
  onNewNote,
  onCapture,
  onPack,
  canEdit,
  canAdmin,
  currentPage,
  history,
  onRecordSearch,
}: {
  onClose: () => void;
  onNavigate: (page: string) => void;
  onOpenNote: (id: string) => void;
  onNewNote: (title: string) => void;
  onCapture: () => void;
  onPack: () => void;
  canEdit: boolean;
  canAdmin: boolean;
  currentPage: PageId;
  history: QolHistory;
  onRecordSearch: (query: string) => void;
}) {
  const [query, setQuery] = useState("");
  const [activeId, setActiveId] = useState<string>();
  const knowledgeSearch = useKnowledgeSearch(query, { debounceMs: 180, minLength: 1, limit: 8 });
  const { backdropRef, dialogRef } = useModalInteraction(onClose);
  const normalized = query.trim().toLocaleLowerCase();

  const pageItems = NAV_GROUPS.flatMap((group) => group.items)
    .filter((item) => (item.id !== "agents" || canAdmin) && ((item.id !== "curator" && item.id !== "review") || canEdit));
  const filteredPages = pageItems.filter((item) => !normalized || `${item.label} ${item.id}`.toLocaleLowerCase().includes(normalized));
  const actions = [
    ...(canEdit ? [
      { id: "new-note", label: `Create ${query ? `“${query}”` : "a new note"}`, detail: "Portable Markdown knowledge", icon: FilePlus2, shortcut: "N", run: () => onNewNote(query) },
      { id: "capture", label: "Capture web source", detail: "Extract with provenance", icon: Globe2, shortcut: "C", run: onCapture },
      { id: "pack", label: "Build Context Pack", detail: "Share bounded agent context", icon: Layers3, run: onPack },
    ] : []),
  ].filter((item) => !normalized || `${item.label} ${item.detail}`.toLocaleLowerCase().includes(normalized));

  const itemProps = (id: string) => ({
    id: `command-${id}`,
    "data-command-item": "true",
    className: activeId === `command-${id}` ? "command-active" : undefined,
    onMouseEnter: () => setActiveId(`command-${id}`),
  });

  function act(action: () => void, rememberSearch = false) {
    if (rememberSearch) onRecordSearch(query);
    action();
    onClose();
  }

  function commandKey(event: ReactKeyboardEvent<HTMLInputElement>) {
    if (!["ArrowDown", "ArrowUp", "Home", "End", "Enter"].includes(event.key)) return;
    const items = [...(dialogRef.current?.querySelectorAll<HTMLButtonElement>("[data-command-item]") ?? [])];
    if (!items.length) return;
    if (event.key === "Enter") {
      event.preventDefault();
      (items.find((item) => item.id === activeId) ?? items[0])?.click();
      return;
    }
    event.preventDefault();
    const current = Math.max(0, items.findIndex((item) => item.id === activeId));
    const index = event.key === "Home" ? 0 : event.key === "End" ? items.length - 1 : (current + (event.key === "ArrowDown" ? 1 : -1) + items.length) % items.length;
    const next = items[index];
    setActiveId(next.id);
    next.scrollIntoView({ block: "nearest" });
  }

  function mark(value: string) {
    if (!normalized) return value;
    const index = value.toLocaleLowerCase().indexOf(normalized);
    if (index < 0) return value;
    return <>{value.slice(0, index)}<mark>{value.slice(index, index + normalized.length)}</mark>{value.slice(index + normalized.length)}</>;
  }

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const first = dialogRef.current?.querySelector<HTMLButtonElement>("[data-command-item]");
      setActiveId(first?.id);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [dialogRef, knowledgeSearch.items, query]);

  const searchStatus = knowledgeSearch.searching
    ? "Searching knowledge…"
    : knowledgeSearch.error
      ? knowledgeSearch.error
      : knowledgeSearch.truncated
        ? `Showing ${knowledgeSearch.items.length} top results from a bounded candidate window. Add another term to narrow it.`
        : knowledgeSearch.active && !knowledgeSearch.items.length
          ? "No approved knowledge matched that search."
          : knowledgeSearch.active
            ? `${knowledgeSearch.items.length} of ${knowledgeSearch.total} ranked matches.`
            : undefined;

  return (
    <div ref={backdropRef} className="command-backdrop" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && onClose()}>
      <section ref={dialogRef} tabIndex={-1} className="command-palette" role="dialog" aria-modal="true" aria-label="Command palette" aria-busy={knowledgeSearch.searching}>
        <div className="command-input"><Search size={19} /><input value={query} onChange={(event) => setQuery(event.target.value)} onKeyDown={commandKey} placeholder="Search knowledge or type a command…" aria-label="Search knowledge or type a command" aria-controls="command-results" aria-activedescendant={activeId} autoComplete="off" />{query ? <button type="button" className="command-clear" aria-label="Clear command search" onClick={() => setQuery("")}><X size={15} /></button> : null}<kbd>esc</kbd></div>
        {searchStatus ? <div className={cx("command-search-status", knowledgeSearch.error && "is-error")} role={knowledgeSearch.error ? "alert" : "status"}>{searchStatus}</div> : null}
        <div id="command-results">
          {!normalized && history.favoritePages.length ? <div className="command-section"><span>Favorites</span><div className="command-nav">{history.favoritePages.flatMap((id) => {
            const item = pageItems.find((candidate) => candidate.id === id);
            if (!item) return [];
            const Icon = item.icon;
            return <button {...itemProps(`favorite-${id}`)} key={id} type="button" onClick={() => act(() => onNavigate(id))}><Star size={14} fill="currentColor" /><Icon size={15} />{item.label}</button>;
          })}</div></div> : null}
          {!normalized && history.recentNotes.length ? <div className="command-section"><span>Recent knowledge</span>{history.recentNotes.slice(0, 5).map((note) => <button {...itemProps(`recent-note-${note.id}`)} key={note.id} type="button" onClick={() => act(() => onOpenNote(note.id))}><i className={`node-dot type-${note.type}`} /><div><strong>{note.title}</strong><small>{note.type} · opened recently</small></div><Braces size={15} /></button>)}</div> : null}
          {!normalized && history.recentPages.length ? <div className="command-section"><span>Recent pages</span><div className="command-nav">{history.recentPages.slice(0, 5).flatMap((id) => {
            const item = pageItems.find((candidate) => candidate.id === id);
            if (!item) return [];
            const Icon = item.icon;
            return <button {...itemProps(`recent-page-${id}`)} key={id} type="button" onClick={() => act(() => onNavigate(id))}><Icon size={15} />{item.label}</button>;
          })}</div></div> : null}
          {!normalized && history.recentSearches.length ? <div className="command-section"><span>Recent searches</span><div className="command-nav">{history.recentSearches.slice(0, 5).map((value, index) => <button {...itemProps(`recent-search-${index}`)} key={value} type="button" onClick={() => setQuery(value)}><Search size={14} />{value}</button>)}</div></div> : null}
          {knowledgeSearch.items.length ? <div className="command-section"><span>Knowledge · {knowledgeSearch.items.length}</span>{knowledgeSearch.items.slice(0, 8).map((note) => <button {...itemProps(`note-${note.id}`)} key={note.id} type="button" onClick={() => act(() => onOpenNote(note.id), true)}><i className={`node-dot type-${note.type}`} /><div><strong>{mark(note.title)}</strong><small>{note.type} · {note.tags.map((tag) => `#${tag}`).join(" ")}</small></div><Braces size={15} /></button>)}</div> : null}
          {actions.length ? <div className="command-section"><span>Actions</span>{actions.map(({ id, label, detail, icon: Icon, shortcut, run }) => <button {...itemProps(`action-${id}`)} key={id} type="button" onClick={() => act(run)}><Icon size={17} /><div><strong>{mark(label)}</strong><small>{detail}</small></div>{shortcut ? <kbd>{shortcut}</kbd> : null}</button>)}</div> : null}
          {filteredPages.length ? <div className="command-section"><span>Go to</span><div className="command-nav">{filteredPages.map(({ id, label, icon: Icon }) => <button {...itemProps(`page-${id}`)} key={id} type="button" aria-current={currentPage === id ? "page" : undefined} onClick={() => act(() => onNavigate(id))}><Icon size={15} />{mark(label)}</button>)}</div></div> : null}
          {normalized && !knowledgeSearch.searching && !knowledgeSearch.items.length && !actions.length && !filteredPages.length && canEdit ? <div className="command-section command-empty"><span>No direct match</span><button {...itemProps("empty-create")} type="button" onClick={() => act(() => onNewNote(query))}><FilePlus2 size={17} /><div><strong>Create “{query}”</strong><small>Start a new memory with this title</small></div></button></div> : null}
        </div>
        <footer><span><kbd>↑</kbd><kbd>↓</kbd> choose</span><span><kbd>↵</kbd> open</span><span><kbd>esc</kbd> close</span><span><Command size={12} /> Lattice command</span></footer>
      </section>
    </div>
  );
}

function HelpModal({ onClose }: { onClose: () => void }) {
  return (
    <Modal title="Move at thought speed" eyebrow="Keyboard & integration" onClose={onClose}>
      <div className="shortcut-list">
        <div><span>Open command palette</span><code><kbd>Ctrl</kbd><b>+</b><kbd>K</kbd></code></div>
        <div><span>Open command palette (alternate)</span><code><kbd>Ctrl</kbd><b>+</b><kbd>Shift</kbd><b>+</b><kbd>P</kbd></code></div>
        <div><span>Refresh workspace data</span><code><kbd>Ctrl</kbd><b>+</b><kbd>Shift</kbd><b>+</b><kbd>R</kbd></code></div>
        <div><span>Collapse or expand navigation</span><code><kbd>Ctrl</kbd><b>+</b><kbd>B</kbd></code></div>
        <div><span>Jump to navigation item 1–9</span><code><kbd>Alt</kbd><b>+</b><kbd>1…9</kbd></code></div>
        <div><span>Create a note</span><code><kbd>N</kbd></code></div>
        <div><span>Capture a link</span><code><kbd>C</kbd></code></div>
        <div><span>Search from anywhere</span><code><kbd>/</kbd></code></div>
        <div><span>Open this keyboard guide</span><code><kbd>?</kbd></code></div>
        <div><span>Close an overlay</span><code><kbd>Esc</kbd></code></div>
      </div>
      <div className="help-concept"><span><Sparkles size={20} /></span><div><strong>Start with a Context Pack.</strong><p>The graph helps you understand knowledge; the pack is the stable, versioned object agents actually share.</p></div></div>
    </Modal>
  );
}
